import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Check, Plus, Save, Users } from 'lucide-react';
import {
  PAYROLL_STATUSES,
  PAYROLL_STATUS_LABELS,
  formatCents,
  type Employee,
  type Paginated,
  type Payroll,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { blank, formatDate } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { StatusBadge, type BadgeKind } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Drawer, Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { MoneyInput } from '@/components/MoneyInput';
import { EmptyState, ErrorState, LoadingState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { useChartAccounts, useCostCenters, todayISO } from '@/lib/queries';
import { DateInput } from '@/components/DateInput';

const STATUS_TONE: Record<string, BadgeKind> = {
  RASCUNHO: 'mute',
  APROVADA: 'info',
  PAGA: 'ok',
  CANCELADA: 'mute',
};

/** Competência corrente no formato AAAA-MM. */
function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

/**
 * Folha de pagamento da obra.
 *
 * Aprovar a folha GERA um contas a pagar no centro de custo escolhido — é
 * assim que mão de obra entra no custo da obra junto com material, em vez de
 * viver numa planilha à parte. Depois de aprovada a folha não é editável; o
 * caminho de volta é cancelar (bloqueado se a conta já teve baixa).
 */
export function FolhaPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [filling, setFilling] = useState(false);
  const [approving, setApproving] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const listQuery = useQuery({
    queryKey: ['payrolls', { status, search, page }],
    queryFn: () =>
      api.get<Paginated<Payroll>>('/payrolls', {
        status: status || undefined,
        search: search || undefined,
        page,
        perPage: 25,
      }),
  });

  const detailQuery = useQuery({
    queryKey: ['payroll', selectedId],
    queryFn: () => api.get<Payroll>(`/payrolls/${selectedId}`),
    enabled: Boolean(selectedId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['payrolls'] });
    void queryClient.invalidateQueries({ queryKey: ['payroll'] });
    void queryClient.invalidateQueries({ queryKey: ['financial-entries'] });
    void queryClient.invalidateQueries({ queryKey: ['overview'] });
  };

  const cancelMutation = useMutation({
    mutationFn: (reason: string) => api.post(`/payrolls/${selectedId}/cancel`, { reason }),
    onSuccess: () => {
      setCancelling(false);
      refresh();
    },
  });

  const payroll = detailQuery.data;

  const columns: Array<Column<Payroll>> = [
    { key: 'referenceMonth', label: 'Competência', mono: true, strong: true, render: (row) => row.referenceMonth },
    { key: 'costCenterName', label: 'Centro de custo', render: (row) => blank(row.costCenterName) },
    {
      key: 'employeeCount',
      label: 'Colaboradores',
      right: true,
      mono: true,
      render: (row) => row.employeeCount,
    },
    {
      key: 'totalGrossCents',
      label: 'Bruto',
      right: true,
      mono: true,
      render: (row) => formatCents(row.totalGrossCents),
    },
    {
      key: 'totalDiscountCents',
      label: 'Descontos',
      right: true,
      mono: true,
      render: (row) => formatCents(row.totalDiscountCents),
    },
    {
      key: 'totalNetCents',
      label: 'Líquido',
      right: true,
      mono: true,
      strong: true,
      render: (row) => formatCents(row.totalNetCents),
    },
    {
      key: 'status',
      label: 'Situação',
      render: (row) => (
        <StatusBadge kind={STATUS_TONE[row.status] ?? 'mute'}>
          {PAYROLL_STATUS_LABELS[row.status]}
        </StatusBadge>
      ),
    },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Folha de pagamento"
        actions={
          can('folha-pagamento:criar') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Abrir folha do mês
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        <FilterBar
          placeholder="Buscar por competência…"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          filters={[
            {
              key: 'status',
              label: 'Situação',
              value: status,
              options: PAYROLL_STATUSES.map((value) => ({
                value,
                label: PAYROLL_STATUS_LABELS[value],
              })),
              onChange: (value) => {
                setStatus(value);
                setPage(1);
              },
            },
          ]}
        />

        {listQuery.isPending && <TableSkeleton columns={7} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Folhas de pagamento"
            columns={columns}
            rows={listQuery.data.data}
            meta={listQuery.data.meta}
            selectedId={selectedId}
            onRowClick={(row) => setSelectedId(row.id)}
            onPageChange={setPage}
            emptyState={
              <EmptyState
                title="Nenhuma folha aberta"
                description="Abra a folha da competência, preencha os colaboradores e aprove."
                action={
                  can('folha-pagamento:criar') ? (
                    <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                      Abrir folha do mês
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        )}

        {selectedId && (
          <div style={{ marginTop: 18 }}>
            {detailQuery.isPending && <LoadingState label="Abrindo a folha…" />}
            {payroll && (
              <PayrollDetail
                payroll={payroll}
                canFill={can('folha-pagamento:editar') && payroll.status === 'RASCUNHO'}
                canApprove={can('folha-pagamento:aprovar') && payroll.status === 'RASCUNHO'}
                canCancel={can('folha-pagamento:excluir') && payroll.status !== 'CANCELADA'}
                onFill={() => setFilling(true)}
                onApprove={() => setApproving(true)}
                onCancel={() => setCancelling(true)}
                onClose={() => setSelectedId(null)}
              />
            )}
          </div>
        )}
      </div>

      <NewPayrollModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false);
          setSelectedId(id);
          refresh();
        }}
      />

      {payroll && (
        <>
          <FillPayrollDrawer
            open={filling}
            payroll={payroll}
            onClose={() => setFilling(false)}
            onSaved={() => {
              setFilling(false);
              refresh();
            }}
          />
          <ApprovePayrollModal
            open={approving}
            payroll={payroll}
            onClose={() => setApproving(false)}
            onDone={() => {
              setApproving(false);
              refresh();
            }}
          />
        </>
      )}

      <ConfirmDialog
        open={cancelling}
        destructive
        requireReason
        loading={cancelMutation.isPending}
        title="Cancelar esta folha?"
        confirmLabel="Cancelar folha"
        message={
          <>
            O lançamento gerado no contas a pagar também é cancelado. Se a conta
            já teve baixa, o cancelamento é recusado — nesse caso, estorne a
            baixa primeiro.
            {cancelMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(cancelMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setCancelling(false);
          cancelMutation.reset();
        }}
        onConfirm={(reason) => cancelMutation.mutate(reason ?? '')}
      />
    </>
  );
}

function PayrollDetail({
  payroll,
  canFill,
  canApprove,
  canCancel,
  onFill,
  onApprove,
  onCancel,
  onClose,
}: {
  payroll: Payroll;
  canFill: boolean;
  canApprove: boolean;
  canCancel: boolean;
  onFill: () => void;
  onApprove: () => void;
  onCancel: () => void;
  onClose: () => void;
}) {
  return (
    <section className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '14px 16px',
          borderBottom: `1px solid ${T.line}`,
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>
            Folha {payroll.referenceMonth}{' '}
            <StatusBadge kind={STATUS_TONE[payroll.status] ?? 'mute'}>
              {PAYROLL_STATUS_LABELS[payroll.status]}
            </StatusBadge>
          </div>
          <div className="mono" style={{ fontSize: 12, color: T.inkFaint }}>
            {blank(payroll.costCenterName)} · {blank(payroll.chartAccountName)}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {canFill && (
          <Button small icon={Users} onClick={onFill}>
            Preencher colaboradores
          </Button>
        )}
        {canApprove && (
          <Button small variant="primary" icon={Check} onClick={onApprove} disabled={payroll.items.length === 0}>
            Aprovar folha
          </Button>
        )}
        {canCancel && (
          <Button small variant="danger" icon={Ban} onClick={onCancel}>
            Cancelar
          </Button>
        )}
        <Button small variant="ghost" onClick={onClose}>
          Fechar
        </Button>
      </div>

      {payroll.items.length === 0 ? (
        <div style={{ padding: 24 }}>
          <EmptyState
            title="Folha ainda sem colaboradores"
            description="Preencha os valores de cada colaborador para poder aprovar."
            action={
              canFill ? (
                <Button variant="primary" icon={Users} onClick={onFill}>
                  Preencher colaboradores
                </Button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <caption className="sr-only">Folha de pagamento por colaborador</caption>
            <thead>
              <tr>
                <th>Colaborador</th>
                <th>Cargo</th>
                <th className="is-right">Base</th>
                <th className="is-right">Extras</th>
                <th className="is-right">Bruto</th>
                <th className="is-right">Descontos</th>
                <th className="is-right">Líquido</th>
              </tr>
            </thead>
            <tbody>
              {payroll.items.map((item) => (
                <tr key={item.id}>
                  <td className="is-strong">{blank(item.employeeName)}</td>
                  <td>{blank(item.positionName)}</td>
                  <td className="is-right mono">{formatCents(item.baseSalaryCents)}</td>
                  <td className="is-right mono">
                    {formatCents(item.overtimeCents + item.bonusesCents)}
                  </td>
                  <td className="is-right mono">{formatCents(item.grossCents)}</td>
                  <td className="is-right mono" style={{ color: T.red }}>
                    {formatCents(item.discountsCents)}
                  </td>
                  <td className="is-right mono is-strong">{formatCents(item.netCents)}</td>
                </tr>
              ))}
              <tr>
                <td className="is-strong">Total</td>
                <td />
                <td />
                <td />
                <td className="is-right mono is-strong">{formatCents(payroll.totalGrossCents)}</td>
                <td className="is-right mono is-strong" style={{ color: T.red }}>
                  {formatCents(payroll.totalDiscountCents)}
                </td>
                <td className="is-right mono is-strong">{formatCents(payroll.totalNetCents)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {payroll.financialEntryId && (
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${T.line}`, fontSize: 12.5 }}>
          <StatusBadge kind="ok">Lançada no contas a pagar</StatusBadge>
          <span className="muted" style={{ marginLeft: 8 }}>
            {payroll.approvedAt ? `aprovada em ${formatDate(payroll.approvedAt)}` : ''}
          </span>
        </div>
      )}

      {payroll.cancelReason && (
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${T.line}`, fontSize: 12.5, color: T.amber }}>
          Cancelada: {payroll.cancelReason}
        </div>
      )}
    </section>
  );
}

function NewPayrollModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const costCenters = useCostCenters();
  const chartAccounts = useChartAccounts('DESPESA');

  const [referenceMonth, setReferenceMonth] = useState(currentMonth());
  const [costCenterId, setCostCenterId] = useState('');
  const [chartAccountId, setChartAccountId] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setReferenceMonth(currentMonth());
      setCostCenterId('');
      setChartAccountId('');
    }
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post<Payroll>('/payrolls', body),
    onSuccess: (created) => onCreated(created.id),
  });

  const errors = fieldErrors(mutation.error);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Abrir folha do mês"
      description="O centro de custo é obrigatório — é nele que a folha vira custo"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!costCenterId || !chartAccountId}
            loading={mutation.isPending}
            onClick={() => mutation.mutate({ referenceMonth, costCenterId, chartAccountId })}
          >
            Abrir folha
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field label="Competência" htmlFor="pay-month" error={errors.referenceMonth}>
          <input
            id="pay-month"
            className="input input--mono"
            type="month"
            value={referenceMonth}
            onChange={(event) => setReferenceMonth(event.target.value)}
          />
        </Field>

        <Field label="Centro de custo" htmlFor="pay-cc" error={errors.costCenterId}>
          <select
            id="pay-cc"
            className="input"
            value={costCenterId}
            onChange={(event) => setCostCenterId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {(costCenters.data ?? []).map((center) => (
              <option key={center.id} value={center.id}>
                {center.code} · {center.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Conta do plano" htmlFor="pay-account" error={errors.chartAccountId}>
          <select
            id="pay-account"
            className="input"
            value={chartAccountId}
            onChange={(event) => setChartAccountId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {(chartAccounts.data ?? []).map((account) => (
              <option key={account.id} value={account.id}>
                {account.code} · {account.name}
              </option>
            ))}
          </select>
        </Field>
      </FormSection>
    </Modal>
  );
}

interface PayrollLine {
  baseSalaryCents: number | null;
  overtimeCents: number | null;
  bonusesCents: number | null;
  inssCents: number | null;
  irrfCents: number | null;
  otherDiscountsCents: number | null;
}

const EMPTY_LINE: PayrollLine = {
  baseSalaryCents: null,
  overtimeCents: null,
  bonusesCents: null,
  inssCents: null,
  irrfCents: null,
  otherDiscountsCents: null,
};

function net(line: PayrollLine): number {
  const gross = (line.baseSalaryCents ?? 0) + (line.overtimeCents ?? 0) + (line.bonusesCents ?? 0);
  const discounts = (line.inssCents ?? 0) + (line.irrfCents ?? 0) + (line.otherDiscountsCents ?? 0);
  return gross - discounts;
}

/**
 * Preenchimento da folha.
 *
 * Começa com todos os colaboradores ativos e o salário do cadastro já lançado
 * como base — o RH ajusta horas extras e descontos em vez de digitar a folha
 * inteira do zero todo mês. Desmarcar tira o colaborador desta competência.
 */
function FillPayrollDrawer({
  open,
  payroll,
  onClose,
  onSaved,
}: {
  open: boolean;
  payroll: Payroll;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [lines, setLines] = useState<Record<string, PayrollLine>>({});
  const [included, setIncluded] = useState<Set<string>>(new Set());
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const employeesQuery = useQuery({
    queryKey: ['employees', 'payroll'],
    queryFn: () => api.get<Paginated<Employee>>('/employees', { perPage: 200 }),
    enabled: open,
  });

  const employees = (employeesQuery.data?.data ?? []).filter(
    (employee) => employee.status !== 'DESLIGADO',
  );

  const formKey = open ? `${payroll.id}:${employeesQuery.dataUpdatedAt}` : null;
  if (formKey !== loadedFor && employees.length > 0) {
    setLoadedFor(formKey);
    const existing = new Map(payroll.items.map((item) => [item.employeeId, item]));
    setIncluded(
      new Set(
        payroll.items.length > 0
          ? payroll.items.map((item) => item.employeeId)
          : employees.map((employee) => employee.id),
      ),
    );
    setLines(
      Object.fromEntries(
        employees.map((employee) => {
          const item = existing.get(employee.id);
          return [
            employee.id,
            item
              ? {
                  baseSalaryCents: item.baseSalaryCents,
                  overtimeCents: item.overtimeCents,
                  bonusesCents: item.bonusesCents,
                  inssCents: item.inssCents,
                  irrfCents: item.irrfCents,
                  otherDiscountsCents: item.otherDiscountsCents,
                }
              : { ...EMPTY_LINE, baseSalaryCents: employee.salaryCents },
          ];
        }),
      ),
    );
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.put(`/payrolls/${payroll.id}/items`, body),
    onSuccess: onSaved,
  });

  const patch = (id: string, value: Partial<PayrollLine>) =>
    setLines((current) => ({ ...current, [id]: { ...(current[id] ?? EMPTY_LINE), ...value } }));

  const chosen = employees.filter((employee) => included.has(employee.id));
  const totalNet = chosen.reduce((acc, employee) => acc + net(lines[employee.id] ?? EMPTY_LINE), 0);
  const negative = chosen.some((employee) => net(lines[employee.id] ?? EMPTY_LINE) < 0);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Preencher folha ${payroll.referenceMonth}`}
      description="Já vem com o salário do cadastro — ajuste extras e descontos"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            icon={Save}
            disabled={chosen.length === 0 || negative}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                items: chosen.map((employee) => {
                  const line = lines[employee.id] ?? EMPTY_LINE;
                  return {
                    employeeId: employee.id,
                    baseSalaryCents: line.baseSalaryCents ?? 0,
                    overtimeCents: line.overtimeCents ?? 0,
                    bonusesCents: line.bonusesCents ?? 0,
                    inssCents: line.inssCents ?? 0,
                    irrfCents: line.irrfCents ?? 0,
                    otherDiscountsCents: line.otherDiscountsCents ?? 0,
                  };
                }),
              })
            }
          >
            Salvar folha
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      {employeesQuery.isPending && <LoadingState label="Carregando colaboradores…" />}

      {employees.map((employee) => {
        const line = lines[employee.id] ?? EMPTY_LINE;
        const value = net(line);
        const isIn = included.has(employee.id);
        return (
          <div
            key={employee.id}
            className="card"
            style={{ padding: 12, marginBottom: 10, opacity: isIn ? 1 : 0.55 }}
          >
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={isIn}
                onChange={(event) =>
                  setIncluded((current) => {
                    const next = new Set(current);
                    if (event.target.checked) next.add(employee.id);
                    else next.delete(employee.id);
                    return next;
                  })
                }
              />
              {employee.registration} · {employee.name}
              <span className="muted" style={{ fontWeight: 400 }}>
                {employee.positionName ?? ''}
              </span>
            </label>

            {isIn && (
              <>
                <div className="grid-2" style={{ marginTop: 10 }}>
                  <Field label="Salário base" htmlFor={`pl-base-${employee.id}`}>
                    <MoneyInput
                      id={`pl-base-${employee.id}`}
                      value={line.baseSalaryCents}
                      onChange={(v) => patch(employee.id, { baseSalaryCents: v })}
                    />
                  </Field>
                  <Field label="Horas extras" htmlFor={`pl-ot-${employee.id}`}>
                    <MoneyInput
                      id={`pl-ot-${employee.id}`}
                      value={line.overtimeCents}
                      onChange={(v) => patch(employee.id, { overtimeCents: v })}
                    />
                  </Field>
                </div>
                <div className="grid-2">
                  <Field label="Bonificações" htmlFor={`pl-bonus-${employee.id}`}>
                    <MoneyInput
                      id={`pl-bonus-${employee.id}`}
                      value={line.bonusesCents}
                      onChange={(v) => patch(employee.id, { bonusesCents: v })}
                    />
                  </Field>
                  <Field label="INSS" htmlFor={`pl-inss-${employee.id}`}>
                    <MoneyInput
                      id={`pl-inss-${employee.id}`}
                      value={line.inssCents}
                      onChange={(v) => patch(employee.id, { inssCents: v })}
                    />
                  </Field>
                </div>
                <div className="grid-2">
                  <Field label="IRRF" htmlFor={`pl-irrf-${employee.id}`}>
                    <MoneyInput
                      id={`pl-irrf-${employee.id}`}
                      value={line.irrfCents}
                      onChange={(v) => patch(employee.id, { irrfCents: v })}
                    />
                  </Field>
                  <Field label="Outros descontos" htmlFor={`pl-other-${employee.id}`}>
                    <MoneyInput
                      id={`pl-other-${employee.id}`}
                      value={line.otherDiscountsCents}
                      onChange={(v) => patch(employee.id, { otherDiscountsCents: v })}
                    />
                  </Field>
                </div>

                <p
                  className="mono"
                  style={{ fontSize: 13, marginTop: 4, color: value < 0 ? T.red : T.inkSoft }}
                >
                  Líquido: <strong>{formatCents(value)}</strong>
                  {value < 0 && ' — os descontos não podem passar do bruto'}
                </p>
              </>
            )}
          </div>
        );
      })}

      <p className="mono" style={{ fontSize: 13.5, marginTop: 8 }}>
        {chosen.length} colaborador(es) · líquido total <strong>{formatCents(totalNet)}</strong>
      </p>
    </Drawer>
  );
}

function ApprovePayrollModal({
  open,
  payroll,
  onClose,
  onDone,
}: {
  open: boolean;
  payroll: Payroll;
  onClose: () => void;
  onDone: () => void;
}) {
  const [dueDate, setDueDate] = useState(todayISO());
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDueDate(payroll.paymentDate?.slice(0, 10) ?? todayISO());
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post(`/payrolls/${payroll.id}/approve`, body),
    onSuccess: onDone,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Aprovar folha"
      description={`Lança ${formatCents(payroll.totalNetCents)} em contas a pagar — líquido de ${payroll.employeeCount} colaborador(es)`}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button variant="primary" loading={mutation.isPending} onClick={() => mutation.mutate({ dueDate })}>
            Aprovar e lançar
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <p style={{ fontSize: 13, color: T.inkSoft, marginBottom: 14, lineHeight: 1.5 }}>
        A aprovação cria um lançamento de <strong>{formatCents(payroll.totalNetCents)}</strong> no
        contas a pagar, no centro de custo <strong>{blank(payroll.costCenterName)}</strong>. Depois
        disso a folha não pode mais ser editada.
      </p>

      <FormSection>
        <Field label="Vencimento do pagamento" htmlFor="approve-due">
          <DateInput
            id="approve-due"
            className="input input--mono"
            value={dueDate}
            onChange={(iso) => setDueDate(iso)}
          />
        </Field>
      </FormSection>
    </Modal>
  );
}
